import get from "lodash.get";
import moment from "moment";
import { REHYDRATE } from "redux-persist";

import AuthenticationActionTypes from "services/authentication/types";
import ContractActionTypes from "services/contract/types";
import CustomAnalysisActionTypes from "./types";
import CustomAnalysisUtils from "./utils";

const CustomAnalysisReducer = (state, action) => {
  const date = moment();
  const dateMinusOneMonth = moment()
    .subtract(1, "days")
    .subtract(1, "month")
    .startOf("day");

  const initialState = {
    attendance: {
      initialized: false,
      filters: {
        dimension: "days",
        all_time_record: true,
        all_time_average: true,
        period_average: true,
      },
    },
    attendanceStats: {
      initialized: false,
      filters: {
        dimension: "dow",
      },
    },
    dowAttendance: {
      initialized: false,
      filters: {
        dimension: "dow",
      },
    },
    hourlyAttendance: {
      initialized: false,
      filters: {
        dimension: "hours",
        dow: [1, 2, 3, 4, 5, 6, 7],
        period_average: true,
        all_time_record: true,
        all_time_average: true,
        group_by_hours: true,
      },
    },
    attendancePeak: {
      initialized: false,
      filters: {
        dimension: "days",
        all_time_record: true,
      },
    },
    pressure: {
      initialized: false,
    },
    dowPressure: {
      initialized: false,
      filters: {
        dimension: "dow",
      },
    },
    filters: {
      analysisAreaGroups: undefined,
      areas: [],
      selectedAreas: [],
      from: parseInt(dateMinusOneMonth.unix()),
      to: parseInt(moment().subtract(1, "days").endOf("day").unix()),
      withComparison: true,
      comparedFrom: parseInt(
        CustomAnalysisUtils.subtractTwoMonthToSameDay(
          dateMinusOneMonth.day(),
          date
        )
      ),
    },
    autoRefresh: false,
  };

  if (!state) {
    state = initialState;
  }

  switch (action.type) {
    case REHYDRATE:
      return {
        ...initialState,
        filters: {
          ...get(initialState, "filters", {}),
          analysisAreaGroups: get(
            action,
            "payload.CustomAnalysis.filters.analysisAreaGroups",
            get(initialState, "filters.analysisAreaGroups", {})
          ),
          selectedAreas: get(
            action,
            "payload.CustomAnalysis.filters.selectedAreas",
            get(initialState, "filters.selectedAreas", [])
          ),
          from: get(
            action,
            "payload.CustomAnalysis.filters.from",
            get(initialState, "filters.from", 0)
          ),
          to: get(
            action,
            "payload.CustomAnalysis.filters.to",
            get(initialState, "filters.to", 0)
          ),
          withComparison: get(
            action,
            "payload.CustomAnalysis.filters.withComparison",
            get(initialState, "filters.withComparison", false)
          ),
          comparedFrom: get(
            action,
            "payload.CustomAnalysis.filters.comparedFrom",
            get(initialState, "filters.comparedFrom", 0)
          ),
        },
      };
    case ContractActionTypes.SWITCH_CONTRACT:
    case AuthenticationActionTypes.LOG_OUT:
      return initialState;
    case CustomAnalysisActionTypes.SET_LOADING:
      return {
        ...state,
        [action.statName]: {
          ...state[action.statName],
          initialized: false,
        },
      };
    case CustomAnalysisActionTypes.SET_AUTO_REFRESH:
      return {
        ...state,
        autoRefresh: action.autoRefresh,
      };
    case CustomAnalysisActionTypes.SET_FILTERS:
      return {
        ...state,
        filters: action.data,
      };
    case CustomAnalysisActionTypes.SET_FILTER:
      return {
        ...state,
        filters: {
          ...state.filters,
          [action.key]: action.value,
        },
      };
    case CustomAnalysisActionTypes.SET_ATTENDANCE:
      return {
        ...state,
        attendance: {
          ...state.attendance,
          ...action.data,
          filters: {
            ...state.attendance.filters,
            ...get(action, "data.filters", {}),
          },
          initialized: true,
        },
      };
    case CustomAnalysisActionTypes.SET_ATTENDANCE_STATS:
      return {
        ...state,
        attendanceStats: {
          ...state.attendanceStats,
          ...action.data,
          filters: {
            ...state.attendanceStats.filters,
            ...get(action, "data.filters", {}),
          },
          initialized: true,
        },
      };
    case CustomAnalysisActionTypes.SET_ATTENDANCE_FILTERS:
      return {
        ...state,
        attendance: {
          ...state.attendance,
          filters: {
            ...state.attendance.filters,
            ...get(action, "data", {}),
          },
          initialized: true,
        },
      };
    case CustomAnalysisActionTypes.SET_ATTENDANCE_PEAK_FILTERS:
      return {
        ...state,
        attendancePeak: {
          ...state.attendancePeak,
          filters: {
            ...state.attendancePeak.filters,
            ...get(action, "data", {}),
          },
          initialized: true,
        },
      };
    case CustomAnalysisActionTypes.SET_HOURLY_ATTENDANCE_FILTERS:
      return {
        ...state,
        hourlyAttendance: {
          ...state.hourlyAttendance,
          filters: {
            ...state.hourlyAttendance.filters,
            ...get(action, "data", {}),
          },
          initialized: true,
        },
      };
    case CustomAnalysisActionTypes.SET_HOURLY_ATTENDANCE:
      return {
        ...state,
        hourlyAttendance: {
          ...state.hourlyAttendance,
          ...action.data,
          filters: {
            ...state.hourlyAttendance.filters,
          },
          initialized: true,
        },
      };
    case CustomAnalysisActionTypes.SET_DOW_ATTENDANCE:
      return {
        ...state,
        dowAttendance: {
          ...state.dowAttendance,
          ...action.data,
          filters: {
            ...state.dowAttendance.filters,
          },
          initialized: true,
        },
      };
    case CustomAnalysisActionTypes.SET_ATTENDANCE_PEAK:
      return {
        ...state,
        attendancePeak: {
          ...state.attendancePeak,
          ...action.data,
          filters: {
            ...state.attendancePeak.filters,
          },
          initialized: true,
        },
      };
    case CustomAnalysisActionTypes.SET_PRESSURE:
      return {
        ...state,
        pressure: {
          ...state.pressure,
          ...action.data,
          filters: {
            ...state.pressure.filters,
          },
          initialized: true,
        },
      };
    case CustomAnalysisActionTypes.SET_DOW_PRESSURE:
      return {
        ...state,
        dowPressure: {
          ...state.dowPressure,
          ...action.data,
          filters: {
            ...state.dowPressure.filters,
          },
          initialized: true,
        },
      };
    case CustomAnalysisActionTypes.SET_INITIALIZED:
      return {
        ...state,
        initialized: action.value,
      };
    default:
      return state;
  }
};

export default CustomAnalysisReducer;
